import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogIn, Loader2, CircleAlert } from "lucide-react";
import { useAuth } from "@/auth";
import { api, formatApiError } from "@/api";

const StarMark = () => (
  <svg viewBox="0 0 64 64" className="h-6 w-6" aria-hidden="true">
    <path
      d="M32 12l5.7 11.5 12.7 1.9-9.2 9 2.2 12.7L32 41.4 20.6 47.1l2.2-12.7-9.2-9 12.7-1.9z"
      fill="#C25E38"
    />
  </svg>
);

const destinationFor = (u) =>
  u.role === "superadmin" ? "/superadmin" : `/admin/${u.restaurant_id}`;

// Errores transitorios: el servidor está arrancando en frío (el preview se
// suspende tras horas sin uso) o hay un fallo de red puntual
const isTransient = (err) =>
  !err.response ||
  err.code === "ECONNABORTED" ||
  [502, 503, 504].includes(err.response?.status);

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export default function Login() {
  const { login, user, status } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [waking, setWaking] = useState(false);

  useEffect(() => {
    if (status === "in" && user) navigate(destinationFor(user), { replace: true });
  }, [status, user, navigate]);

  // "Despierta" el backend nada más abrir la página: si el entorno lleva
  // horas sin uso, el arranque en frío tarda ~1 minuto. Este ping inicia
  // el arranque mientras el usuario escribe sus credenciales.
  useEffect(() => {
    api.get("/").catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError("");
    setLoading(true);
    setWaking(false);
    // El primer acceso del día puede coincidir con el arranque en frío del
    // servidor: reintentamos automáticamente (~2 min) en lugar de fallar.
    const maxAttempts = 10;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const u = await login(email, password);
        navigate(destinationFor(u), { replace: true });
        return;
      } catch (err) {
        if (isTransient(err) && attempt < maxAttempts) {
          setWaking(true);
          await sleep(attempt <= 2 ? 4000 : 10000);
          continue;
        }
        setWaking(false);
        if (isTransient(err)) {
          // Agotados los reintentos: el servidor sigue arrancando
          setError("El servidor está arrancando y tarda más de lo normal. Espera unos segundos y vuelve a intentarlo.");
        } else if (err.response.status === 401) {
          // Rechazo de autenticación REAL (solo esto significa credenciales incorrectas)
          setError(formatApiError(err, "Credenciales incorrectas."));
        } else {
          // Cualquier otro fallo (429, 403 del edge/Cloudflare, 5xx): error temporal, no de credenciales
          setError(formatApiError(err, "El servicio no está disponible temporalmente. Inténtalo de nuevo en unos segundos."));
        }
        setLoading(false);
        return;
      }
    }
  };

  return (
    <div className="relative min-h-screen bg-[#FDFBF7] text-[#1C1917] flex items-center justify-center px-5">
      <div className="ambient-glow" />
      <div className="grain-overlay" />

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="relative z-10 w-full max-w-sm"
        data-testid="login-page"
      >
        <div className="flex flex-col items-center text-center gap-3 mb-8">
          <StarMark />
          <p className="font-mono-label text-[10px] uppercase tracking-[0.3em] text-[#B38D46]">
            Review QR
          </p>
          <h1 className="font-serif-display text-3xl sm:text-4xl font-medium tracking-tight leading-snug">
            Acceso <em className="text-[#C25E38]">privado</em>
          </h1>
          <p className="text-sm text-[#57534E]">
            Inicia sesión para gestionar tus opiniones.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          data-testid="login-form"
          className="bg-white border border-[#E7E2D7] rounded-2xl p-6 sm:p-7 shadow-[0_10px_30px_-10px_rgba(28,25,23,0.06)] flex flex-col gap-4"
        >
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="login-email"
              className="font-mono-label text-[10px] uppercase tracking-[0.25em] text-[#A8A29E] px-1"
            >
              Email
            </label>
            <input
              id="login-email"
              data-testid="login-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@email.com"
              required
              className="w-full rounded-xl border border-[#E7E2D7] bg-white/90 px-4 py-3 text-sm text-[#1C1917] placeholder:text-[#A8A29E] focus:outline-none focus:ring-2 focus:ring-[#C25E38]/30 focus:border-[#C25E38]/50 transition-shadow duration-200"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="login-password"
              className="font-mono-label text-[10px] uppercase tracking-[0.25em] text-[#A8A29E] px-1"
            >
              Contraseña
            </label>
            <input
              id="login-password"
              data-testid="login-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              className="w-full rounded-xl border border-[#E7E2D7] bg-white/90 px-4 py-3 text-sm text-[#1C1917] placeholder:text-[#A8A29E] focus:outline-none focus:ring-2 focus:ring-[#C25E38]/30 focus:border-[#C25E38]/50 transition-shadow duration-200"
            />
          </div>

          {waking && !error && (
            <div
              data-testid="login-waking"
              role="status"
              className="flex items-start gap-2.5 text-sm text-[#57534E] bg-[#B38D46]/8 border border-[#B38D46]/25 rounded-xl px-4 py-3"
            >
              <Loader2 className="h-4 w-4 mt-0.5 shrink-0 animate-spin" />
              El servidor está arrancando tras un periodo de inactividad. Reintentando automáticamente, no cierres la página…
            </div>
          )}

          {error && (
            <div
              data-testid="login-error"
              role="alert"
              className="flex items-start gap-2.5 text-sm text-[#A84C2A] bg-[#C25E38]/8 border border-[#C25E38]/25 rounded-xl px-4 py-3"
            >
              <CircleAlert className="h-4 w-4 mt-0.5 shrink-0" />
              {error}
            </div>
          )}

          <button
            type="submit"
            data-testid="login-submit-button"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2.5 bg-[#1C1917] hover:bg-[#C25E38] disabled:bg-[#E7E5E4] disabled:text-[#A8A29E] text-[#FDFBF7] font-medium text-base rounded-xl px-6 py-3.5 transition-colors duration-300 cursor-pointer mt-1"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" data-testid="login-loading" />
            ) : (
              <LogIn className="h-4 w-4" />
            )}
            {loading ? "Entrando..." : "Iniciar sesión"}
          </button>
        </form>

        <p className="text-center font-mono-label text-[9px] uppercase tracking-[0.25em] text-[#A8A29E] mt-6">
          Acceso gestionado por el administrador
        </p>
      </motion.div>
    </div>
  );
}
